import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { ThemeProvider } from '@/lib/theme'
import { toLocale, useI18n } from '@/lib/i18n'
import { useDocumentMeta } from '@/lib/useDocumentMeta'
import { breadcrumbSchema, sandboxMeta } from '@/lib/seo'
import JsonLd from '@/components/seo/JsonLd'
import { readSession, type SandboxSession } from '@/lib/sandbox'
import { getProject } from '@/lib/sandboxProjects'
import SandboxStart from '@/components/sandbox/SandboxStart'
import SandboxFrame from '@/components/sandbox/SandboxFrame'
import '@/components/sandbox/Sandbox.css'

/**
 * /demo/:project — the interactive sandbox for one portfolio project.
 *
 * Without a session: the start screen (what it is, pick a role, setup).
 * With one: a control panel on top and the chosen app underneath — a static
 * sandbox build in an iframe, or a native app streamed from a cloud device.
 */
export default function SandboxPage() {
  const { project: id } = useParams()
  const project = useMemo(() => getProject(id), [id])
  const { to } = useI18n()
  if (!project) return <Navigate to={to('/projects')} replace />
  return <SandboxInner projectId={project.id} />
}

function SandboxInner({ projectId }: { projectId: string }) {
  const project = getProject(projectId)!
  const locale = toLocale(useI18n().lang)
  useDocumentMeta({ ...sandboxMeta(project.shop.name, locale), path: project.startPath, locale })

  // Read in the effect below, not on the first render: that render must
  // match the prerendered start screen (built without sessionStorage).
  const [session, setSession] = useState<SandboxSession | null>(null)

  useEffect(() => setSession(readSession(project)), [project])

  // Another tab may end or replace the session.
  const sync = useCallback(() => setSession(readSession(project)), [project])
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === project.sessionKey) sync()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [sync, project])

  return (
    <ThemeProvider>
      {session ? (
        <SandboxFrame project={project} session={session} onSessionChange={setSession} />
      ) : (
        <SandboxStart project={project} onStarted={setSession} />
      )}
      <JsonLd data={breadcrumbSchema(project.startPath, locale)!} />
    </ThemeProvider>
  )
}
